import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { 
  Search, 
  Plus, 
  FileDown, 
  Edit2, 
  Trash2, 
  Archive,
  X,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  ArrowLeft,
  GitBranch
} from 'lucide-react';
import api from '../components/api';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';

const ListeFilierePage = () => {
  const navigate = useNavigate();
  const [filieres, setFilieres] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isArchiveModalOpen, setIsArchiveModalOpen] = useState(false);
  const [selectedFiliere, setSelectedFiliere] = useState(null);
  const [formData, setFormData] = useState({
    libelle: ''
  });
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  useEffect(() => {
    fetchFilieres();
  }, [currentPage, pageSize]);

  const fetchFilieres = async () => {
    try {
      const response = await api.get(`/filiere/liste-filieres/?page=${currentPage}&page_size=${pageSize}`);
      
      // Traiter la réponse même si elle est vide
      const filieresData = response.data?.results || [];
      const totalCount = response.data?.count || 0;
      
      setFilieres(filieresData);
      setTotalPages(Math.ceil(totalCount / pageSize));
    } catch (error) {
      console.error('Erreur:', error);
      
      // Ne pas traiter les 404 comme des erreurs critiques
      if (error.response && error.response.status === 404) {
        setFilieres([]);
        setTotalPages(1);
      } else {
        toast.error('Erreur lors du chargement des filières');
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (selectedFiliere) {
        await api.put(`/filiere/details-filieres/${selectedFiliere.id}/`, formData);
        toast.success('Filière mise à jour avec succès');
      } else {
        await api.post('/filiere/liste-filieres/', formData);
        toast.success('Filière ajoutée avec succès');
      }
      setIsModalOpen(false);
      setFormData({ libelle: '' });
      setSelectedFiliere(null);
      fetchFilieres();
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Une erreur est survenue';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/filiere/details-filieres/${selectedFiliere.id}/`);
      toast.success('Filière supprimée avec succès');
      setIsDeleteModalOpen(false);
      setSelectedFiliere(null);
      fetchFilieres();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleArchive = async () => {
    try {
      await api.post(`/filiere/archiver-filiere/${selectedFiliere.id}/archiver/`);
      toast.success('Filière archivée avec succès');
      setIsArchiveModalOpen(false);
      setSelectedFiliere(null);
      fetchFilieres();
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Erreur lors de l\'archivage';
      toast.error(errorMessage);
    }
  };

  const handleExport = async (format) => {
    try {
      console.log('Tentative d\'export en format:', format);
      const response = await api.post('/filiere/export-filiere/', {
        format: format
      }, {
        responseType: 'blob'
      });
      
      console.log('Réponse reçue:', response);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `filieres.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`Export ${format.toUpperCase()} réussi`);
    } catch (error) {
      console.error('Erreur lors de l\'export:', error);
      toast.error(`Erreur lors de l'export ${format.toUpperCase()}`);
    }
  };

  const filteredFilieres = filieres.filter(filiere =>
    filiere.libelle.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Layout>
      <div className="min-h-screen bg-gray-50">
        <div className="container mx-auto px-4 py-8">
          {/* Card principale avec design professionnel */}
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
            {/* Header avec bannière bleue */}
            <div className="bg-gradient-to-r from-[#202654] to-[#202654]/90 px-6 py-8">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                <div className="flex items-center text-white">
                  <button
                    onClick={() => navigate(-1)}
                    className="mr-4 p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-all duration-200 backdrop-blur-sm"
                  >
                    <ArrowLeft className="h-5 w-5" />
                  </button>
                  <div className="bg-white/10 p-3 rounded-xl mr-4">
                    <GitBranch className="h-8 w-8 text-white" />
                  </div>
                  <div>
                    <h1 className="text-2xl font-bold">Gestion des Filières</h1>
                    <p className="mt-1 text-blue-100">
                      Gérez les filières et leurs spécialités
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Contenu principal */}
            <div className="p-6">
              <div className="max-w-7xl mx-auto">
                {/* Contenu existant */}
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-lg font-semibold text-gray-900">Liste des filières</h2>
                  <button
                    onClick={() => {
                      setSelectedFiliere(null);
                      setFormData({ libelle: '' });
                      setIsModalOpen(true);
                    }}
                    className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#202654] hover:bg-[#202654]/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#202654]"
                  >
                    <Plus className="h-5 w-5 mr-2" />
                    Ajouter une filière
                  </button>
                </div>

                <div className="flex flex-col sm:flex-row gap-4 mb-6">
                  <div className="relative w-64">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Search className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-[#202654] focus:border-[#202654] sm:text-sm"
                      placeholder="Rechercher..."
                    />
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleExport('xlsx')}
                      className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#202654]"
                    >
                      <FileDown className="h-5 w-5 mr-2" />
                      Excel
                    </button>
                    <button
                      onClick={() => handleExport('pdf')}
                      className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#202654]"
                    >
                      <FileDown className="h-5 w-5 mr-2" />
                      PDF
                    </button>
                    <button
                      onClick={() => handleExport('csv')}
                      className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#202654]"
                    >
                      <FileDown className="h-5 w-5 mr-2" />
                      CSV
                    </button>
                  </div>
                </div>

                <div className="flex flex-col">
                  <div className="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                    <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
                      <div className="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Libellé
                              </th>
                              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Niveaux
                              </th>
                              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Élèves
                              </th>
                              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Date création
                              </th>
                              <th scope="col" className="relative px-6 py-3">
                                <span className="sr-only">Actions</span>
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {filteredFilieres.map((filiere) => (
                              <tr key={filiere.id}>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                  {filiere.libelle}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm">
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                    {filiere.nombre_niveaux} niveaux
                                  </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm">
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                    {filiere.nombre_eleves} etudiants
                                  </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  {new Date(filiere.create_at).toLocaleDateString()}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                  <div className="flex justify-end space-x-2">
                                    <button
                                      onClick={() => {
                                        setSelectedFiliere(filiere);
                                        setFormData({ libelle: filiere.libelle });
                                        setIsModalOpen(true);
                                      }}
                                      className="text-[#202654] hover:text-[#202654]/80"
                                    >
                                      <Edit2 className="h-5 w-5" />
                                    </button>
                                    {!filiere.is_archive ? (
                                      <button
                                        onClick={() => {
                                          setSelectedFiliere(filiere);
                                          setIsArchiveModalOpen(true);
                                        }}
                                        className="text-yellow-600 hover:text-yellow-800"
                                      >
                                        <Archive className="h-5 w-5" />
                                      </button>
                                    ) : (
                                      <button
                                        onClick={() => {
                                          setSelectedFiliere(filiere);
                                          setIsArchiveModalOpen(true);
                                        }}
                                        className="text-green-600 hover:text-green-800"
                                      >
                                        <Archive className="h-5 w-5" />
                                      </button>
                                    )}
                                    <button
                                      onClick={() => {
                                        setSelectedFiliere(filiere);
                                        setIsDeleteModalOpen(true);
                                      }}
                                      className="text-red-600 hover:text-red-800"
                                    >
                                      <Trash2 className="h-5 w-5" />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Modals existants */}
        {isModalOpen && (
          <div className="fixed z-10 inset-0 overflow-y-auto">
            <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
              <div className="fixed inset-0 transition-opacity" aria-hidden="true">
                <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
              </div>
              <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
              <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="sm:flex sm:items-start">
                    <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                      <div className="flex justify-between items-center mb-4">
                        <h3 className="text-lg leading-6 font-medium text-gray-900">
                          {selectedFiliere ? 'Modifier la filière' : 'Ajouter une filière'}
                        </h3>
                        <button
                          onClick={() => {
                            setIsModalOpen(false);
                            setSelectedFiliere(null);
                            setFormData({ libelle: '' });
                          }}
                          className="text-gray-400 hover:text-gray-500"
                        >
                          <X className="h-6 w-6" />
                        </button>
                      </div>
                      <form onSubmit={handleSubmit} className="mt-4">
                        <div>
                          <label htmlFor="libelle" className="block text-sm font-medium text-gray-700 mb-1">
                            Libellé
                          </label>
                          <input
                            type="text"
                            name="libelle"
                            id="libelle"
                            value={formData.libelle}
                            onChange={(e) => setFormData({ ...formData, libelle: e.target.value })}
                            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#202654] focus:border-[#202654] sm:text-sm transition-colors duration-200"
                            placeholder="Entrez le libellé de la filière"
                            required
                          />
                        </div>
                        <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                          <button
                            type="submit"
                            disabled={loading}
                            className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#202654] hover:bg-[#202654]/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#202654] sm:ml-3 sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                          >
                            {loading ? (
                              <>
                                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Enregistrement...
                              </>
                            ) : (
                              selectedFiliere ? 'Modifier' : 'Ajouter'
                            )}
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setIsModalOpen(false);
                              setSelectedFiliere(null);
                              setFormData({ libelle: '' });
                            }}
                            className="mt-3 w-full inline-flex justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#202654] sm:mt-0 sm:ml-3 sm:w-auto transition-colors duration-200"
                          >
                            Annuler
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {isDeleteModalOpen && (
          <div className="fixed z-10 inset-0 overflow-y-auto">
            <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
              <div className="fixed inset-0 transition-opacity" aria-hidden="true">
                <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
              </div>
              <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
              <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="sm:flex sm:items-start">
                    <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                      <AlertCircle className="h-6 w-6 text-red-600" />
                    </div>
                    <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                      <h3 className="text-lg leading-6 font-medium text-gray-900">
                        Supprimer la filière
                      </h3>
                      <div className="mt-2">
                        <p className="text-sm text-gray-500">
                          Êtes-vous sûr de vouloir supprimer la filière "{selectedFiliere?.libelle}" ? Cette action est irréversible.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="button"
                    onClick={handleDelete}
                    className="w-full inline-flex justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto transition-colors duration-200"
                  >
                    Supprimer
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setIsDeleteModalOpen(false);
                      setSelectedFiliere(null);
                    }}
                    className="mt-3 w-full inline-flex justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#202654] sm:mt-0 sm:ml-3 sm:w-auto transition-colors duration-200"
                  >
                    Annuler
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {isArchiveModalOpen && (
          <div className="fixed z-10 inset-0 overflow-y-auto">
            <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
              <div className="fixed inset-0 transition-opacity" aria-hidden="true">
                <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
              </div>
              <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
              <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="sm:flex sm:items-start">
                    <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100 sm:mx-0 sm:h-10 sm:w-10">
                      <Archive className="h-6 w-6 text-yellow-600" />
                    </div>
                    <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                      <h3 className="text-lg leading-6 font-medium text-gray-900">
                        Archiver la filière
                      </h3>
                      <div className="mt-2">
                        <p className="text-sm text-gray-500">
                          Êtes-vous sûr de vouloir archiver la filière "{selectedFiliere?.libelle}" ? Cette action est réversible.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="button"
                    onClick={handleArchive}
                    className="w-full inline-flex justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-yellow-600 hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 sm:ml-3 sm:w-auto transition-colors duration-200"
                  >
                    Archiver
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setIsArchiveModalOpen(false);
                      setSelectedFiliere(null);
                    }}
                    className="mt-3 w-full inline-flex justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#202654] sm:mt-0 sm:ml-3 sm:w-auto transition-colors duration-200"
                  >
                    Annuler
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default ListeFilierePage;
